import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, AlertTriangle, User, Users, MessageSquare, Shield, Trash2, MailCheck, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const StaffDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [reports, setReports] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New states for Users and Rooms management
  const [permissions, setPermissions] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Email verification state
  const [verificationRequests, setVerificationRequests] = useState([]);
  
  // Create user state
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    display_name: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'staff') {
      navigate('/chat');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      // Load permissions first - this is critical
      let permData = {};
      try {
        const permRes = await axios.get(`${API}/staff/${user.id}/permissions`);
        permData = permRes.data;
        setPermissions(permData);
      } catch (error) {
        console.error('Failed to load permissions:', error);
        toast.error('Failed to load permissions. Contact admin.');
        setPermissions({});
      }
      
      // Load other data - failures here are not critical
      try {
        const [feedbackRes, reportsRes, logsRes] = await Promise.all([
          axios.get(`${API}/feedback`).catch(() => ({ data: [] })),
          axios.get(`${API}/reports`).catch(() => ({ data: [] })),
          axios.get(`${API}/logs/audit`).catch(() => ({ data: [] }))
        ]);
        
        setFeedback(feedbackRes.data);
        setReports(reportsRes.data);
        setAuditLogs(logsRes.data);
      } catch (error) {
        console.error('Failed to load feedback/reports/logs:', error);
      }
      
      // Load users and rooms if staff has permissions
      if (permData.can_create_users || permData.can_ban_users || permData.can_view_chat_logs) {
        try {
          const usersRes = await axios.get(`${API}/admin/users/all`);
          setAllUsers(usersRes.data);
        } catch (error) {
          console.error('Failed to load users:', error);
          setAllUsers([]);
        }
      }
      
      if (permData.can_manage_rooms || permData.can_view_room_logs) {
        try {
          const roomsRes = await axios.get(`${API}/admin/rooms/all`);
          setRooms(roomsRes.data);
        } catch (error) {
          console.error('Failed to load rooms:', error);
          setRooms([]);
        }
      }
      
      // Load verification requests if staff has permission
      if (permData.can_grant_email_verification) {
        try {
          const verificationRes = await axios.get(`${API}/admin/verification-requests`);
          setVerificationRequests(verificationRes.data || []);
        } catch (error) {
          console.error('Failed to load verification requests:', error);
          setVerificationRequests([]);
        }
      }
    } catch (error) {
      console.error('Critical error in loadData:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status?status=${status}`);
      toast.success('Feedback status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resolveReport = async (reportId) => {
    try {
      await axios.put(`${API}/reports/${reportId}/resolve`);
      toast.success('Report resolved');
      loadData();
    } catch (error) {
      toast.error('Failed to resolve report');
    }
  };

  // User Management Functions
  const banUser = async (userId) => {
    if (!permissions.can_ban_users) {
      toast.error('You do not have permission to ban users');
      return;
    }
    
    if (!window.confirm('Are you sure you want to ban this user?')) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`);
      toast.success('User banned successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to ban user');
    }
  };

  const unbanUser = async (userId) => {
    if (!permissions.can_ban_users) {
      toast.error('You do not have permission to unban users');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`);
      toast.success('User unbanned successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to unban user');
    }
  };

  const deleteUser = async (userId) => {
    if (!permissions.can_create_users) {
      toast.error('You do not have permission to delete users');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const createUser = async () => {
    if (!permissions.can_create_users) {
      toast.error('You do not have permission to create users');
      return;
    }
    
    // Validate input
    if (!newUserData.username || !newUserData.email || !newUserData.password) {
      toast.error('Username, email, and password are required');
      return;
    }
    
    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/create`, {
        username: newUserData.username,
        email: newUserData.email,
        password: newUserData.password,
        display_name: newUserData.display_name || newUserData.username,
        role: 'registered'
      });
      toast.success(`User "${newUserData.username}" created successfully`);
      setShowCreateUserDialog(false);
      setNewUserData({ username: '', email: '', password: '', display_name: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Room Management Functions
  const deleteRoom = async (roomId) => {
    if (!permissions.can_manage_rooms) {
      toast.error('You do not have permission to delete rooms');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this room? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/rooms/${roomId}`);
      toast.success('Room deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  // Email verification request functions
  const grantVerification = async (requestId, username) => {
    if (!window.confirm(`Are you sure you want to grant access to ${username}?`)) return;
    try {
      await axios.post(`${API}/admin/verification-requests/${requestId}/grant`);
      toast.success(`Access granted to ${username}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const denyVerification = async (requestId, username) => {
    const reason = window.prompt(`Why are you denying verification for ${username}?`);
    if (reason === null) return; // User cancelled
    try {
      await axios.post(`${API}/admin/verification-requests/${requestId}/deny`, { reason });
      toast.success(`Request denied for ${username}`);
      loadData();
    } catch (error) {
      toast.error('Failed to deny request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-2 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8" />
              Staff Dashboard
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Welcome, {user?.username}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Permissions Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Your Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {permissions.can_create_users && <Badge variant="default">Create/Remove Users</Badge>}
              {permissions.can_ban_users && <Badge variant="default">Ban/Unban Users</Badge>}
              {permissions.can_view_chat_logs && <Badge variant="default">View Chat Logs</Badge>}
              {permissions.can_view_room_logs && <Badge variant="default">View Room Logs</Badge>}
              {permissions.can_manage_rooms && <Badge variant="default">Manage Rooms</Badge>}
              {permissions.can_grant_email_verification && <Badge variant="default">Grant Email Verification</Badge>}
              {Object.values(permissions).every(p => !p) && (
                <Badge variant="secondary">No permissions assigned yet</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="feedback" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5 mb-4 sm:mb-6 gap-1">
            <TabsTrigger value="feedback" data-testid="feedback-tab">Feedback</TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">Reports</TabsTrigger>
            {(permissions.can_create_users || permissions.can_ban_users) && (
              <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            )}
            {(permissions.can_manage_rooms || permissions.can_view_room_logs) && (
              <TabsTrigger value="rooms" data-testid="rooms-tab">Rooms</TabsTrigger>
            )}
            {permissions.can_grant_email_verification && (
              <TabsTrigger value="verification" data-testid="verification-tab">Email Verification</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="feedback">
            <Card>
              <CardHeader>
                <CardTitle>User Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] sm:h-[500px] md:h-[600px]">
                  <div className="space-y-4">
                    {feedback.map((fb) => (
                      <div key={fb.id} className="p-4 border rounded-lg bg-white" data-testid={`feedback-${fb.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{fb.sender?.username || fb.sender_email || 'Guest User'}</p>
                            <p className="text-xs text-gray-500">{new Date(fb.created_at).toLocaleString()}</p>
                            {fb.source === 'forgot_password' && (
                              <Badge variant="outline" className="mt-1">Forgot Password</Badge>
                            )}
                          </div>
                          <Badge variant={fb.status === 'pending' ? 'default' : 'secondary'}>
                            {fb.status}
                          </Badge>
                        </div>
                        <p className="text-gray-700 mb-3 whitespace-pre-line">{fb.message}</p>
                        {fb.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateFeedbackStatus(fb.id, 'reviewed')}
                              data-testid={`mark-reviewed-${fb.id}`}
                            >
                              Mark Reviewed
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateFeedbackStatus(fb.id, 'resolved')}
                              data-testid={`mark-resolved-${fb.id}`}
                            >
                              Mark Resolved
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {feedback.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No feedback yet</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle>User Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] sm:h-[500px] md:h-[600px]">
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div key={report.id} className="p-4 border rounded-lg bg-white" data-testid={`report-${report.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">
                              {report.reporter?.username} reported {report.reported_user?.username}
                            </p>
                            <p className="text-xs text-gray-500">{new Date(report.created_at).toLocaleString()}</p>
                          </div>
                          <Badge variant={report.status === 'pending' ? 'destructive' : 'secondary'}>
                            {report.status}
                          </Badge>
                        </div>
                        <p className="text-gray-700 mb-3"><strong>Reason:</strong> {report.reason}</p>
                        {report.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => resolveReport(report.id)}
                            data-testid={`resolve-report-${report.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Resolve Report
                          </Button>
                        )}
                      </div>
                    ))}
                    {reports.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No reports yet</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Management Tab */}
          {(permissions.can_create_users || permissions.can_ban_users) && (
            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    User Management
                    {permissions.can_create_users && (
                      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
                        <DialogTrigger asChild>
                          <Button>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Create User
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Create New User</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div>
                              <Label htmlFor="username">Username *</Label>
                              <Input
                                id="username"
                                value={newUserData.username}
                                onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                                placeholder="Enter username"
                              />
                            </div>
                            <div>
                              <Label htmlFor="email">Email *</Label>
                              <Input
                                id="email"
                                type="email"
                                value={newUserData.email}
                                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                                placeholder="Enter email"
                              />
                            </div>
                            <div>
                              <Label htmlFor="password">Password *</Label>
                              <PasswordInput
                                id="password"
                                value={newUserData.password}
                                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                                placeholder="Enter password (min 6 characters)"
                              />
                            </div>
                            <div>
                              <Label htmlFor="display_name">Display Name (Optional)</Label>
                              <Input
                                id="display_name"
                                value={newUserData.display_name}
                                onChange={(e) => setNewUserData({...newUserData, display_name: e.target.value})}
                                placeholder="Enter display name"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                              Cancel
                            </Button>
                            <Button onClick={createUser}>
                              Create User
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Input
                      placeholder="Search users..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="max-w-sm"
                    />
                  </div>
                  <ScrollArea className="h-[400px] sm:h-[500px] md:h-[600px]">
                    <div className="space-y-4">
                      {allUsers
                        .filter(u => 
                          // Exclude super admin and staff from staff dashboard view
                          u.role !== 'super_admin' && u.role !== 'staff' &&
                          (u.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                          (u.display_name && u.display_name.toLowerCase().includes(userSearchQuery.toLowerCase())))
                        )
                        .map((user) => (
                          <div key={user.id} className="p-4 border rounded-lg bg-white" data-testid={`user-${user.id}`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  {user.display_name || user.username}
                                </p>
                                <p className="text-sm text-gray-600">@{user.username}</p>
                                <p className="text-xs text-gray-500">
                                  Joined: {new Date(user.created_at).toLocaleDateString()}
                                </p>
                                <div className="flex gap-2 mt-2">
                                  <Badge variant={user.role === 'admin' ? 'destructive' : user.role === 'staff' ? 'default' : 'secondary'}>
                                    {user.role}
                                  </Badge>
                                  {user.is_banned && <Badge variant="destructive">Banned</Badge>}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {permissions.can_ban_users && (
                                  <>
                                    {user.is_banned ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => unbanUser(user.id)}
                                        data-testid={`unban-user-${user.id}`}
                                      >
                                        Unban
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => banUser(user.id)}
                                        data-testid={`ban-user-${user.id}`}
                                      >
                                        Ban
                                      </Button>
                                    )}
                                  </>
                                )}
                                {permissions.can_create_users && user.role !== 'admin' && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => deleteUser(user.id)}
                                    data-testid={`delete-user-${user.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      {allUsers.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No users found</p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Rooms Management Tab */}
          {(permissions.can_manage_rooms || permissions.can_view_room_logs) && (
            <TabsContent value="rooms">
              <Card>
                <CardHeader>
                  <CardTitle>Room Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] sm:h-[500px] md:h-[600px]">
                    <div className="space-y-4">
                      {rooms.map((room) => (
                        <div key={room.id} className="p-4 border rounded-lg bg-white" data-testid={`room-${room.id}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                {room.name}
                              </p>
                              <p className="text-sm text-gray-600">{room.description || 'No description'}</p>
                              <p className="text-xs text-gray-500">
                                Created: {new Date(room.created_at).toLocaleDateString()}
                              </p>
                              <div className="flex gap-2 mt-2">
                                <Badge variant="outline">
                                  <Users className="w-3 h-3 mr-1" />
                                  {room.participant_count || 0} members
                                </Badge>
                                {room.is_private && <Badge variant="secondary">Private</Badge>}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {permissions.can_view_room_logs && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/room-logs/${room.id}`)}
                                  data-testid={`view-logs-${room.id}`}
                                >
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  View Logs
                                </Button>
                              )}
                              {permissions.can_manage_rooms && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deleteRoom(room.id)}
                                  data-testid={`delete-room-${room.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {rooms.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No rooms found</p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Email Verification Tab */}
          {permissions.can_grant_email_verification && (
            <TabsContent value="verification">
              <Card>
                <CardHeader>
                  <CardTitle>Email Verification Requests ({verificationRequests.filter(r => r.status === 'pending').length})</CardTitle>
                  <p className="text-sm text-gray-600 mt-2">
                    Manage manual verification requests from users who are having trouble with automatic email verification.
                  </p>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px] sm:h-[500px] md:h-[600px]">
                    <div className="space-y-3">
                      {verificationRequests.filter(r => r.status === 'pending').map((request) => (
                        <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg bg-white shadow-sm">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold text-lg">{request.username}</p>
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                Pending
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{request.email}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              Requested: {new Date(request.request_date).toLocaleString()}
                            </p>
                            <div className="mt-2 text-xs text-gray-500">
                              <span className="font-medium">Current Status:</span>
                              {' '}Email: {request.current_email_verified ? '✓ Verified' : '✗ Unverified'}
                              {', '}Account: {request.current_is_verified ? '✓ Verified' : '✗ Unverified'}
                              {', '}Role: {request.current_role}
                            </div>
                            {request.message && (
                              <p className="text-sm text-gray-700 mt-2 italic">
                                "{request.message}"
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              onClick={() => grantVerification(request.id, request.username)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`grant-access-${request.id}`}
                            >
                              <MailCheck className="w-4 h-4 mr-2" />
                              Grant Access
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => denyVerification(request.id, request.username)}
                              data-testid={`deny-${request.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Deny
                            </Button>
                          </div>
                        </div>
                      ))}
                      {verificationRequests.filter(r => r.status === 'pending').length === 0 && (
                        <div className="text-center py-12">
                          <MailCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500 text-lg">No pending verification requests</p>
                          <p className="text-gray-400 text-sm mt-2">
                            Users can request manual verification from the email verification banner on the chat page.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Show processed requests */}
                    {verificationRequests.filter(r => r.status !== 'pending').length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Processed Requests</h3>
                        <div className="space-y-2">
                          {verificationRequests.filter(r => r.status !== 'pending').slice(0, 5).map((request) => (
                            <div key={request.id} className="flex items-center justify-between p-3 border rounded bg-gray-50">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{request.username}</p>
                                  <Badge 
                                    variant={request.status === 'approved' ? 'default' : 'destructive'}
                                    className={request.status === 'approved' ? 'bg-green-600' : ''}
                                  >
                                    {request.status}
                                  </Badge>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {request.status === 'approved' 
                                    ? `Approved on ${new Date(request.approved_at).toLocaleString()}`
                                    : `Denied on ${new Date(request.denied_at).toLocaleString()}`
                                  }
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default StaffDashboard;
